import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * One step of the Enrollment Information flow, as the sidebar shows it.
 *
 * The step list is decided by ProgramEnrollmentPage (it depends on the applicant's answers: nursing, driver's
 * license and background-check pages only apply to some programs), so the page publishes it here and the sidebar,
 * which sits beside the page in DashboardLayout, reads it. The registry's static step list for this section is
 * not used for that reason.
 */
export interface EnrollmentNavStep
{
    /** Stable URL value for `?step=` (see {@link enrollmentStepKey}). */
    key: string;
    title: string;
    /** The step the applicant is on. */
    current: boolean;
    /** Completed or the furthest reached so far, so the applicant may go back to it. Later steps stay locked. */
    reachable: boolean;
}

/** `Driver's License` -> `drivers-license`, `1098-T Opt-In` -> `1098-t-opt-in`. Stable across renders and reloads. */
export const enrollmentStepKey = (title: string): string =>
    title
        .toLowerCase()
        .replace(/['’]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

interface EnrollmentStepsContextValue
{
    steps: EnrollmentNavStep[];
    setSteps: (steps: EnrollmentNavStep[]) => void;
}

const EnrollmentStepsContext = createContext<EnrollmentStepsContextValue>({
    steps: [],
    setSteps: () => { /* outside a provider: nothing is listening */ },
});

const sameSteps = (a: EnrollmentNavStep[], b: EnrollmentNavStep[]) =>
    a.length === b.length &&
    a.every((s, i) =>
        s.key === b[i].key && s.title === b[i].title && s.current === b[i].current && s.reachable === b[i].reachable);

export const EnrollmentStepsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) =>
{
    const [steps, setStepsState] = useState<EnrollmentNavStep[]>([]);

    // Skip no-op updates so a page that republishes on every render cannot cause a render loop.
    const setSteps = useCallback((next: EnrollmentNavStep[]) =>
        setStepsState(prev => (sameSteps(prev, next) ? prev : next)), []);

    const value = useMemo(() => ({ steps, setSteps }), [steps, setSteps]);

    return <EnrollmentStepsContext.Provider value={value}>{children}</EnrollmentStepsContext.Provider>;
};

/** The published Enrollment Information steps; empty unless the enrollment page is mounted and has loaded them. */
export const useEnrollmentSteps = (): EnrollmentNavStep[] => useContext(EnrollmentStepsContext).steps;

/**
 * Publish the page's steps to the sidebar while it is mounted (pass an empty array while they are not final).
 * They are cleared when the page unmounts, so the sidebar shows substeps only on the enrollment route.
 */
export const usePublishEnrollmentSteps = (steps: EnrollmentNavStep[]): void =>
{
    const { setSteps } = useContext(EnrollmentStepsContext);

    useEffect(() => { setSteps(steps); }, [steps, setSteps]);
    useEffect(() => () => setSteps([]), [setSteps]);
};
